import { AppThemeService } from '@affine/core/modules/theme';
import { useService } from '@toeverything/infra';
import { ThemeProvider as NextThemeProvider, useTheme } from 'next-themes';
import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';

const themes = ['dark', 'light'];

function ThemeObserver() {
  const { resolvedTheme } = useTheme();
  const service = useService(AppThemeService);

  useEffect(() => {
    service.appTheme.theme$.next(resolvedTheme);
  }, [resolvedTheme, service.appTheme.theme$]);

  return null;
}

// When embedded (?embed or ?theme=light), force light theme.
// Keeps the canvas visually consistent with the host app regardless of the
// user's OS preference or previous Affine theme choice.
function getForcedTheme(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const themeParam = params.get('theme');
  if (themeParam === 'light' || themeParam === 'dark') return themeParam;
  if (params.has('embed')) return 'light';
  return undefined;
}

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const forcedTheme = getForcedTheme();
  return (
    <NextThemeProvider
      themes={themes}
      enableSystem={true}
      forcedTheme={forcedTheme}
    >
      {children}
      <ThemeObserver />
    </NextThemeProvider>
  );
};
